import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bell,
  Building2,
  Copy,
  Download,
  Loader2,
  Palette,
  ShieldCheck,
  Sparkles,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Field } from '@/components/ui/Field';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { AgentRulesTab } from '@/components/agent/AgentRulesTab';
import { useAuth } from '@/lib/auth-store';
import { api, ApiError } from '@/lib/api';
import { meetsMinRole } from '@/lib/roles';
import { cn } from '@/lib/utils';

/**
 * Phase-1 Settings page — Profile + Password tabs. Org + Notifications tabs
 * land later (Org needs the org-update endpoint, Notifications needs the
 * notification preferences schema, both Phase 2/3 work).
 */
type SettingsTab =
  | 'profile'
  | 'organization'
  | 'vibe-space'
  | 'notifications'
  | 'password'
  | 'agent'
  | 'account';

export function Settings() {
  const { state, refresh } = useAuth();
  const [tab, setTab] = useState<SettingsTab>('profile');

  if (state.status !== 'authenticated') return null;

  const hasOrg = state.profile.primaryOrgId !== null;
  // The Agent rules tab is instructor-and-up — only people who can OWN
  // courses get to subscribe to course events. Students don't.
  const canEditAgent = meetsMinRole(
    state.profile.roles,
    state.profile.primaryOrgId ?? undefined,
    'instructor',
  );
  // The Vibe Space tab is also instructor-and-up — they're the ones with a
  // public branded page to manage. Students have no Vibe Space concept.
  const canEditVibeSpace = hasOrg && canEditAgent;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container max-w-3xl py-10">
        <header className="mb-8 space-y-1">
          <h1 className="font-display text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground">
            {hasOrg ? (
              <>
                Update your profile and security. Org-wide settings are managed under{' '}
                <a className="font-medium text-foreground underline" href="/org-admin">
                  Org Admin
                </a>
                .
              </>
            ) : (
              <>Update your profile and security.</>
            )}
          </p>
        </header>

        <Tabs value={tab} onValueChange={(v) => setTab(v as SettingsTab)}>
          <TabsList>
            <TabsTrigger value="profile">
              <span className="inline-flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5" aria-hidden /> Profile
              </span>
            </TabsTrigger>
            {hasOrg && (
              <TabsTrigger value="organization">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" aria-hidden /> Organization
                </span>
              </TabsTrigger>
            )}
            {canEditVibeSpace && (
              <TabsTrigger value="vibe-space">
                <span className="inline-flex items-center gap-1.5">
                  <Palette className="h-3.5 w-3.5" aria-hidden /> Vibe Space
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications">
              <span className="inline-flex items-center gap-1.5">
                <Bell className="h-3.5 w-3.5" aria-hidden /> Notifications
              </span>
            </TabsTrigger>
            <TabsTrigger value="password">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Password
              </span>
            </TabsTrigger>
            {canEditAgent && (
              <TabsTrigger value="agent">
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden /> Agent rules
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger value="account">
              <span className="inline-flex items-center gap-1.5">
                <Trash2 className="h-3.5 w-3.5" aria-hidden /> Account
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab token={state.token} refreshAuth={refresh} />
          </TabsContent>

          {hasOrg && (
            <TabsContent value="organization">
              <OrganizationTab token={state.token} />
            </TabsContent>
          )}

          {canEditVibeSpace && (
            <TabsContent value="vibe-space">
              <VibeSpaceTab token={state.token} orgId={state.profile.primaryOrgId as string} />
            </TabsContent>
          )}

          <TabsContent value="notifications">
            <NotificationsTab token={state.token} refreshAuth={refresh} />
          </TabsContent>

          <TabsContent value="password">
            <PasswordTab token={state.token} />
          </TabsContent>

          {canEditAgent && (
            <TabsContent value="agent">
              <AgentRulesTab token={state.token} />
            </TabsContent>
          )}

          <TabsContent value="account">
            <AccountTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ─── Profile tab ──────────────────────────────────────────────────────── */

function ProfileTab({ token, refreshAuth }: { token: string; refreshAuth: () => Promise<void> }) {
  const { state } = useAuth();
  if (state.status !== 'authenticated') return null;

  const [fullName, setFullName] = useState(state.profile.fullName);
  const [displayName, setDisplayName] = useState(state.profile.displayName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await api.auth.updateMe(token, {
        fullName: fullName.trim(),
        displayName: displayName.trim() === '' ? null : displayName.trim(),
      });
      await refreshAuth();
      setSuccess('Saved. Your profile is up to date.');
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 400
          ? 'Please check the form and try again.'
          : "EduBorg couldn't save right now. Try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="font-display text-lg font-semibold">Your profile</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Visible to people in your courses and on your Vibe Space.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <Field
          label="Full name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <Field
          label="Display name"
          hint="Optional — shown in lieu of your full name where space is tight."
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Field
          label="Email"
          type="email"
          value={state.profile.email}
          readOnly
          disabled
          hint="Email changes go through a verification flow — coming soon."
        />

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-foreground"
          >
            {success}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </section>
  );
}

/* ─── Password tab ─────────────────────────────────────────────────────── */

function PasswordTab({ token }: { token: string }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    if (currentPassword === newPassword) {
      setError('Pick something different from your current password.');
      return;
    }

    setSubmitting(true);
    try {
      await api.auth.changePassword(token, { currentPassword, newPassword });
      setSuccess('Password updated. Existing sessions stay signed in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setError('Current password is incorrect.');
      } else if (e instanceof ApiError && e.status === 400) {
        setError('Please check the form and try again.');
      } else {
        setError("EduBorg couldn't update your password right now.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="font-display text-lg font-semibold">Change password</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Confirm your current password, then set a new one. We&apos;ll keep your other sessions
        signed in.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          hint="At least 8 characters."
        />
        <Field
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-foreground"
          >
            {success}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </section>
  );
}

/* ─── Organization tab ─────────────────────────────────────────────────── */

function OrganizationTab({ token }: { token: string }) {
  const { state } = useAuth();
  const orgQuery = useQuery({
    queryKey: ['org', 'me'],
    queryFn: () => api.orgs.me(token),
    enabled: token.length > 0,
  });

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (orgQuery.data?.organization) setName(orgQuery.data.organization.name);
  }, [orgQuery.data]);

  if (state.status !== 'authenticated') return null;

  const orgId = state.profile.primaryOrgId;
  const canEdit = orgId ? meetsMinRole(state.profile.roles, orgId, 'admin') : false;

  if (orgQuery.isLoading) {
    return <div className="h-32 animate-pulse rounded-2xl bg-card" />;
  }
  if (orgQuery.isError || !orgQuery.data) {
    return (
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <p className="text-sm text-muted-foreground">Couldn&apos;t load your organization.</p>
      </section>
    );
  }

  const org = orgQuery.data.organization;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await api.orgs.update(token, org.id, { name: name.trim() });
      setSuccess('Organization updated.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError('Only admins can change the organization name.');
      } else {
        setError("EduBorg couldn't save right now.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="font-display text-lg font-semibold">{org.name}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Public URL: <span className="font-mono text-foreground">eduborg.com/{org.slug}</span> ·
        Type: {org.type}
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <Field
          label="Organization name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit}
          hint={
            canEdit
              ? 'Visible to people in your courses, on emails, and on dashboards.'
              : 'Only admins can edit this. Ask one to update if needed.'
          }
        />
        <Field
          label="Public URL slug"
          value={org.slug}
          readOnly
          disabled
          hint="Slug rename is its own audit-logged flow — coming soon."
        />

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-foreground"
          >
            {success}
          </div>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting || name.trim() === org.name}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

/* ─── Vibe Space tab ───────────────────────────────────────────────────── */

/**
 * Edit your org's public branded page (`/space/:orgSlug`). Instructor-or-up
 * with an org context only.
 *
 * Loads from `api.orgs.me` (which returns the org row including the new
 * Vibe Space fields once 0010 is applied) and PATCHes via
 * `api.vibeSpace.update`. Has a "Copy public link" affordance and a
 * visibility toggle so a teacher can hide the page if they don't want
 * one yet.
 */
function VibeSpaceTab({ token, orgId }: { token: string; orgId: string }) {
  const queryClient = useQueryClient();
  const orgQuery = useQuery({
    queryKey: ['org', 'me'],
    queryFn: () => api.orgs.me(token),
    enabled: token.length > 0,
  });

  const [tagline, setTagline] = useState('');
  const [aboutHtml, setAboutHtml] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [heroEmoji, setHeroEmoji] = useState('');
  const [visible, setVisible] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftBrief, setDraftBrief] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (orgQuery.data && !hydrated) {
      const org = orgQuery.data.organization as {
        tagline: string | null;
        aboutHtml: string | null;
        accentColor: string | null;
        heroEmoji: string | null;
        vibeSpaceVisible: boolean;
      };
      setTagline(org.tagline ?? '');
      setAboutHtml(org.aboutHtml ?? '');
      setAccentColor(org.accentColor ?? '');
      setHeroEmoji(org.heroEmoji ?? '');
      setVisible(org.vibeSpaceVisible ?? true);
      setHydrated(true);
    }
  }, [orgQuery.data, hydrated]);

  if (orgQuery.isLoading || !orgQuery.data) {
    return <div className="h-40 animate-pulse rounded-2xl bg-card" aria-live="polite" />;
  }

  const org = orgQuery.data.organization;
  const publicUrl = `${window.location.origin}/space/${org.slug}`;

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await api.vibeSpace.update(token, orgId, {
        tagline: tagline.trim() === '' ? null : tagline.trim(),
        aboutHtml: aboutHtml.trim() === '' ? null : aboutHtml,
        accentColor: accentColor.trim() === '' ? null : accentColor.trim(),
        heroEmoji: heroEmoji.trim() === '' ? null : heroEmoji.trim(),
        vibeSpaceVisible: visible,
      });
      setSuccess('Saved. Your public page is up to date.');
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'invalid_request'
          ? 'Some of the fields look invalid. Check the accent color (hex / hsl / rgb only).'
          : "Couldn't save just now. Try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard blocked — fail silently */
    }
  }

  async function draftWithEduBorg() {
    if (drafting) return;
    setDrafting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.ai.sparkVibeSpace(token, {
        ...(draftBrief.trim() === '' ? {} : { brief: draftBrief.trim() }),
      });
      // Pre-fill the form fields. Don't auto-save — let the instructor
      // tweak before clicking "Save changes". Empty fields stay empty
      // (the model can return null for fields it didn't have a strong
      // opinion on).
      if (res.draft.tagline !== null) setTagline(res.draft.tagline);
      if (res.draft.aboutHtml !== null) setAboutHtml(res.draft.aboutHtml);
      if (res.draft.accentColor !== null) setAccentColor(res.draft.accentColor);
      if (res.draft.heroEmoji !== null) setHeroEmoji(res.draft.heroEmoji);
      setSuccess(
        res.simulated
          ? 'Drafted (simulated — set AIML_API_KEY for real model output). Tweak the fields and Save when ready.'
          : 'Drafted. Tweak the fields and Save when ready.',
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ai_access_denied') {
        setError("You're out of AI credits for this period.");
      } else {
        setError("EduBorg couldn't draft just now. Try again in a moment.");
      }
    } finally {
      setDrafting(false);
    }
  }

  return (
    <section className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h2 className="font-display text-lg font-semibold">Your public page</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          A simple, branded page you can share with prospective students. Your published courses
          appear here with a "Join class" button.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <span className="truncate font-mono text-xs text-muted-foreground">{publicUrl}</span>
          <span className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium hover:bg-secondary"
            >
              <Copy className="h-3 w-3" aria-hidden /> {linkCopied ? 'Copied' : 'Copy'}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium hover:bg-secondary"
            >
              Open
            </a>
          </span>
        </div>
      </div>

      <ImagesPanel
        token={token}
        orgId={orgId}
        logoR2Key={(orgQuery.data.organization as { logoR2Key: string | null }).logoR2Key}
        bannerR2Key={(orgQuery.data.organization as { bannerR2Key: string | null }).bannerR2Key}
        onChanged={() => {
          // Refetch the org so the preview reflects the new key.
          void queryClient.invalidateQueries({ queryKey: ['org', 'me'] });
        }}
      />

      <div className="rounded-2xl border border-accent/30 bg-accent/5 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="font-display text-sm font-semibold">Draft with EduBorg</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Skip the blank page. EduBorg fills in a starter tagline, about-section, accent
                color, and hero emoji you can edit. Costs 2 AI credits.
              </p>
            </div>
            <input
              type="text"
              value={draftBrief}
              onChange={(e) => setDraftBrief(e.target.value)}
              placeholder="Optional: one line about what you teach (e.g. 'AP Biology with a lab focus')"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              maxLength={400}
            />
            <button
              type="button"
              onClick={draftWithEduBorg}
              disabled={drafting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-accent-foreground shadow-sm transition-shadow hover:shadow-md disabled:opacity-50"
            >
              {drafting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {drafting ? 'Drafting…' : 'Draft starter copy'}
            </button>
          </div>
        </div>
      </div>

      <form
        onSubmit={onSave}
        className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4"
      >
        <Field
          label="Tagline"
          hint="Short pitch under your name. ~80 characters works best."
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          maxLength={200}
          placeholder="e.g. AP Biology with Mr. Cato — labs, study sessions, and weekly hints from EduBorg."
        />
        <Field
          label="Hero emoji"
          hint="A single emoji shown next to your name. Optional."
          value={heroEmoji}
          onChange={(e) => setHeroEmoji(e.target.value)}
          maxLength={16}
          placeholder="🧬"
        />
        <Field
          label="Accent color"
          hint="HSL or hex — used as the page accent. Leave blank for the EduBorg default."
          value={accentColor}
          onChange={(e) => setAccentColor(e.target.value)}
          maxLength={32}
          placeholder="#3b82f6 or hsl(218, 92%, 56%)"
        />

        <label className="block">
          <span className="mb-1 block text-sm font-medium">About</span>
          <textarea
            value={aboutHtml}
            onChange={(e) => setAboutHtml(e.target.value)}
            rows={6}
            className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="A few paragraphs about you, your teaching style, what students will learn. Plain text or simple HTML — we'll sanitize before publishing."
            maxLength={20_000}
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            HTML is sanitized server-side — only safe formatting tags survive (paragraphs, bold,
            italic, lists, links).
          </span>
        </label>

        <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={visible}
            onChange={(e) => setVisible(e.target.checked)}
            className="h-4 w-4"
          />
          <span>
            <span className="font-medium">Publish my page</span>
            <span className="ml-1 text-muted-foreground">
              — uncheck to hide and return 404 at the public URL.
            </span>
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {success && (
          <div
            role="status"
            className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-foreground"
          >
            {success}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * Logo + banner upload panel for the Vibe Space tab. File picker per
 * kind, immediate POST to /uploads/org/:id/image, refresh callback so
 * the preview reflects the new key. Server enforces the content-type
 * + size constraints; we surface the readable error if rejected.
 */
function ImagesPanel({
  token,
  orgId,
  logoR2Key,
  bannerR2Key,
  onChanged,
}: {
  token: string;
  orgId: string;
  logoR2Key: string | null;
  bannerR2Key: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<'logo' | 'banner' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(kind: 'logo' | 'banner', file: File) {
    setBusy(kind);
    setError(null);
    try {
      await api.uploads.uploadOrgImage(token, orgId, kind, file);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'unsupported_content_type') {
        setError('PNG / JPEG / WebP / GIF / SVG only.');
      } else if (e instanceof ApiError && e.code === 'file_too_large') {
        setError('Max 2 MB. Resize and try again.');
      } else if (e instanceof ApiError && e.status === 403) {
        setError("You don't have permission to upload images for this org.");
      } else {
        setError("Couldn't upload just now. Try again in a moment.");
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
      <header>
        <h3 className="font-display text-base font-semibold">Logo + banner</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Optional. Logo appears in the hero card; banner shows above it. PNG / JPEG / WebP / GIF /
          SVG, max 2 MB each.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <ImageSlot
          label="Logo"
          imageUrl={logoR2Key ? api.uploads.objectUrl(logoR2Key) : null}
          busy={busy === 'logo'}
          onPick={(file) => void upload('logo', file)}
          aspect="square"
        />
        <ImageSlot
          label="Banner"
          imageUrl={bannerR2Key ? api.uploads.objectUrl(bannerR2Key) : null}
          busy={busy === 'banner'}
          onPick={(file) => void upload('banner', file)}
          aspect="wide"
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function ImageSlot({
  label,
  imageUrl,
  busy,
  onPick,
  aspect,
}: {
  label: string;
  imageUrl: string | null;
  busy: boolean;
  onPick: (file: File) => void;
  aspect: 'square' | 'wide';
}) {
  return (
    <div className="space-y-2">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div
        className={cn(
          'flex w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-background',
          aspect === 'square' ? 'aspect-square' : 'aspect-[4/1]',
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={`${label} preview`} className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-muted-foreground">No {label.toLowerCase()} yet</span>
        )}
      </div>
      <label className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-secondary">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        {busy
          ? 'Uploading…'
          : imageUrl
            ? `Replace ${label.toLowerCase()}`
            : `Upload ${label.toLowerCase()}`}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(file);
            // Reset so picking the same filename twice still fires onChange.
            e.target.value = '';
          }}
        />
      </label>
    </div>
  );
}

/* ─── Notifications tab ────────────────────────────────────────────────── */

type NotificationPreferences = {
  weeklyDigest: boolean;
  inAppMentions: boolean;
  inAppRiskFlags: boolean;
  emailRiskFlags: boolean;
  emailAnnouncements: boolean;
  quietModeEduBorg: boolean;
};

const DEFAULT_PREFS: NotificationPreferences = {
  weeklyDigest: true,
  inAppMentions: true,
  inAppRiskFlags: true,
  emailRiskFlags: false,
  emailAnnouncements: true,
  quietModeEduBorg: false,
};

function parsePrefs(raw: string | null | undefined): NotificationPreferences {
  if (!raw) return DEFAULT_PREFS;
  try {
    const parsed = JSON.parse(raw) as Partial<NotificationPreferences>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function NotificationsTab({
  token,
  refreshAuth,
}: {
  token: string;
  refreshAuth: () => Promise<void>;
}) {
  const { state } = useAuth();
  // Phase-2 short-cut: persist via localStorage so the toggles are real.
  // Server-side persistence wires in once email + push channels turn on (Phase 4).
  const [prefs, setPrefs] = useState<NotificationPreferences>(() =>
    parsePrefs(localStorage.getItem('eduborg.notification-prefs')),
  );
  const [saved, setSaved] = useState(false);

  if (state.status !== 'authenticated') return null;

  const onChange = (key: keyof NotificationPreferences, next: boolean) => {
    const updated = { ...prefs, [key]: next };
    setPrefs(updated);
    localStorage.setItem('eduborg.notification-prefs', JSON.stringify(updated));
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1500);
    void refreshAuth;
    void token;
  };

  const rows: Array<{ key: keyof NotificationPreferences; label: string; sub: string }> = [
    {
      key: 'weeklyDigest',
      label: 'Weekly course-pulse digest',
      sub: 'A Sunday rollup of attendance, risk flags, and milestones.',
    },
    {
      key: 'inAppMentions',
      label: 'In-app mentions',
      sub: 'When someone @-mentions you in a discussion or message.',
    },
    {
      key: 'inAppRiskFlags',
      label: 'In-app risk flags',
      sub: "Flash a notification when EduBorg's risk radar fires.",
    },
    {
      key: 'emailRiskFlags',
      label: 'Email risk flags',
      sub: 'Same flags, also delivered to your inbox.',
    },
    {
      key: 'emailAnnouncements',
      label: 'Email announcements',
      sub: 'Course-wide announcements you want a copy of by email.',
    },
    {
      key: 'quietModeEduBorg',
      label: 'Quiet mode for EduBorg',
      sub: 'Keep the companion visible but mute the proactive whispers.',
    },
  ];

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Notifications</h2>
        {saved && (
          <span className="text-xs text-accent" role="status">
            Saved.
          </span>
        )}
      </header>

      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li key={row.key} className="flex items-start justify-between gap-4 px-5 py-4">
              <div>
                <div className="text-sm font-medium">{row.label}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{row.sub}</div>
              </div>
              <Switch
                value={prefs[row.key]}
                onChange={(v) => onChange(row.key, v)}
                ariaLabel={row.label}
              />
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-muted-foreground">
        Saved locally for now. Server-side persistence + email + push delivery wire in Phase 4.
      </p>
    </section>
  );
}

function Switch({
  value,
  onChange,
  ariaLabel,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      onClick={() => onChange(!value)}
      className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 ${
        value ? 'bg-accent' : 'bg-secondary'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-card shadow transition-transform ${
          value ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/* ─── Account tab — right-to-erasure (FERPA / COPPA / GDPR) ────────────── */

/**
 * The destructive-but-final account-delete control. Kept on its own tab
 * away from "Profile" so a slip of the mouse on Save doesn't trigger a
 * data wipe. The server requires literal `confirmation: 'DELETE'` in the
 * request body, mirrored on the client by a typed-confirmation input —
 * the button stays disabled until the student types DELETE exactly.
 */
function AccountTab() {
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status !== 'authenticated') return null;
  const ready = confirmation.trim() === 'DELETE';

  async function handleDelete() {
    if (!ready || submitting || state.status !== 'authenticated') return;
    setSubmitting(true);
    setError(null);
    try {
      await api.auth.deleteAccount(state.token);
      // Server already revoked the session + scrubbed PII. Bounce the
      // user to landing via signOut so the auth store clears.
      await signOut();
      navigate('/');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'sole_admin_blocks_deletion') {
        setError(
          'You are the only admin in one or more organizations. Promote another admin first, then come back to delete your account.',
        );
      } else if (e instanceof ApiError && e.code === 'cannot_delete_super_admin') {
        setError(
          'Super admin accounts must be deleted by another super admin via the admin console.',
        );
      } else {
        setError("Couldn't delete your account just now. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h2 className="font-display text-xl font-semibold">Your account</h2>
        <p className="text-sm text-muted-foreground">
          Sign out below, or permanently delete your account. See our{' '}
          <a href="/privacy" className="font-medium underline" target="_blank" rel="noreferrer">
            Privacy policy
          </a>{' '}
          for what we keep, scrub, and delete.
        </p>
      </header>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-display text-base font-semibold">Sign out everywhere</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Revokes this session. You'll be signed out and sent back to the landing page.
        </p>
        <button
          type="button"
          onClick={async () => {
            await signOut();
            navigate('/');
          }}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-secondary"
        >
          Sign out
        </button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-display text-base font-semibold">Download your data</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          A JSON file with everything EduBorg stores about you — profile, courses, assessment
          attempts, posts, messages, study sessions, and audit-log entries. Doesn't include other
          users' content, your password, or session tokens.
        </p>
        <DownloadDataButton token={state.token} />
      </div>

      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
        <header className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
          <h3 className="font-display text-base font-semibold text-destructive">
            Delete your account
          </h3>
        </header>
        <p className="mt-2 text-sm text-muted-foreground">
          This permanently removes your password, sessions, AI credits, notifications, and Study
          Room conversations. Your name will appear as <em>Deleted user</em> on courses you created,
          posts you wrote, and messages you sent — those stay so other students and instructors
          aren't broken. Course enrollments are kept (soft-deleted) so the gradebook remains intact.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          You'll be signed out immediately. The email you signed up with is freed up so you can
          register again later if you want.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Type <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-xs">DELETE</code>{' '}
          to confirm.
        </p>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="DELETE"
            aria-label="Type DELETE to confirm"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-destructive/30"
          />
          <button
            type="button"
            onClick={handleDelete}
            disabled={!ready || submitting}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Delete account
          </button>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * "Download my data" trigger. The endpoint streams JSON with a
 * Content-Disposition header, so we hand off to a real <a> click rather
 * than fetch — that way the browser does its native save-as flow and
 * we don't have to construct a Blob object URL ourselves. Bearer auth
 * is on the SPA's localStorage token, which we can't easily attach to
 * a plain anchor — so fetch + Blob URL it is. Trade-off: the whole
 * dump materializes in memory before save. Realistic ceiling per user
 * is well under the Workers 100MB response cap.
 */
function DownloadDataButton({ token }: { token: string }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const base = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');
      const res = await fetch(`${base}/auth/export-data`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!res.ok) {
        if (res.status === 429) {
          setError('Too many export requests. Try again in a minute.');
        } else {
          setError("Couldn't generate your export. Try again in a moment.");
        }
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `eduborg-data-export-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke briefly so Safari has time to start the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("Couldn't reach the server. Try again in a moment.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="mt-3 space-y-2">
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
      >
        {downloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        {downloading ? 'Preparing your export…' : 'Download my data (JSON)'}
      </button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
